"use client"

import Link from "next/link"
import { ExternalLink } from "lucide-react"

import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { toast } from "@/hooks/use-toast"
import { ApiError } from "@/lib/api/client"
import { copyText } from "@/lib/clipboard"

import { useCurrentUser } from "@/features/auth/hooks"
import {
  canModifyInstallmentPlan,
  canVoid,
  canVoidCollectedPlan,
} from "@/features/auth/role"

import { useResendInvoice, useSendInvoice } from "../hooks"
import {
  EMAIL_FAILED_STATUS,
  type BackendStatus,
  type PaymentMode,
} from "../types"
import {
  actionsForStatus,
  editActionFor,
  isRefundable,
  receiptActionFor,
  voidActionFor,
} from "../actions"
import { copyReceiptLink } from "../copy-receipt"
import { payUrlFor, pdfUrlFor, portalUrlFor, receiptPathFor } from "../urls"

export interface InvoiceActionMenuProps {
  invoiceId: string
  invoiceNumber: string
  status: BackendStatus
  /**
   * Drives the Edit item (see ``editActionFor``) and gates the Refund item:
   * only a one-time ("full") invoice is refundable from Billdozer. Required
   * rather than defaulted — a money action must not fall open for a caller
   * that forgets to thread it through.
   */
  paymentMode: PaymentMode
  /** Used by canVoid to decide whether to show mutating items. */
  createdById?: number
  hubspotDealUrl?: string
}

export function InvoiceActionMenu({
  invoiceId,
  invoiceNumber,
  status,
  paymentMode,
  createdById,
  hubspotDealUrl,
}: InvoiceActionMenuProps) {
  const resendMutation = useResendInvoice()
  const sendMutation = useSendInvoice()
  const { data: user } = useCurrentUser()
  const allowMutating = canVoid(
    user,
    createdById !== undefined ? { created_by: { id: createdById } } : null,
  )
  const actions = actionsForStatus(status)
  // A paid plan invoice would otherwise offer Refund and fail with a 409
  // whose copy is about Stripe partial refunds — wrong, and confusing.
  if (!isRefundable(status, paymentMode)) actions.delete("refund")
  // Edit is mode-aware, so it is resolved here rather than read off the
  // status-only ``actions`` set. One href for both destinations — the
  // /amend route re-reads the same gate and picks the right editor.
  const editAction = editActionFor(status, paymentMode)
  const showEdit =
    allowMutating &&
    (editAction === "amend" ||
      // A Sales Rep can't modify a plan (the endpoints are manager-gated),
      // so hide the door rather than send them to the "managers only" state.
      (editAction === "plan" && canModifyInstallmentPlan(user)))
  // Void is mode-aware for the same reason as Edit: a live plan that has
  // collected money is voidable by a Manager only, and never appears in
  // the status-only ``actions`` set.
  const voidAction = voidActionFor(status, paymentMode)
  const showVoid =
    allowMutating &&
    (voidAction === "standard" ||
      (voidAction === "collected" && canVoidCollectedPlan(user)))
  // The whole-invoice receipt copy is mode-aware for the same reason, and
  // deliberately not behind ``allowMutating``: copying a link the customer
  // already holds is a read action, like the copy-pay-link and
  // copy-portal-link items beside it.
  const showReceipt = receiptActionFor(status, paymentMode) === "invoice"
  // EMAIL_FAILED rows go through /send/ — see actions.ts comment.
  const isFirstSend = status === EMAIL_FAILED_STATUS
  const resendBusy = isFirstSend ? sendMutation.isPending : resendMutation.isPending

  const handleResend = () => {
    const onSuccess = () => {
      toast({
        // A never-delivered (EMAIL_FAILED) row is a first send, not a
        // resend — say "Email sent" to match the "Send email" label.
        title: isFirstSend ? "Email sent" : "Email resent",
        description: `Invoice #${invoiceNumber} was sent to the customer.`,
      })
    }
    const onError = (err: unknown) => {
      const detail =
        err instanceof ApiError
          ? (err.detail as { detail?: string } | undefined)?.detail
          : undefined
      toast({
        title: isFirstSend
          ? "Could not send invoice"
          : "Could not resend invoice",
        description: detail ?? "Please try again.",
        variant: "destructive",
      })
    }
    if (isFirstSend) {
      sendMutation.mutate({ id: invoiceId }, { onSuccess, onError })
    } else {
      resendMutation.mutate({ id: invoiceId }, { onSuccess, onError })
    }
  }

  const handleSendEmail = () => {
    sendMutation.mutate(
      { id: invoiceId },
      {
        onSuccess: () => {
          toast({
            title: "Email sent",
            description: `Invoice #${invoiceNumber} was sent to the customer.`,
          })
        },
        onError: (err: unknown) => {
          const detail =
            err instanceof ApiError
              ? (err.detail as { detail?: string } | undefined)?.detail
              : undefined
          toast({
            title: "Could not send invoice",
            description: detail ?? "Please try again.",
            variant: "destructive",
          })
        },
      },
    )
  }

  const handleCopyLink = async () => {
    const ok = await copyText(payUrlFor(invoiceId))
    toast(
      ok
        ? {
            title: "Payment link copied",
            description: "The link is now on your clipboard.",
          }
        : {
            title: "Could not copy",
            description: "Copy the URL from the address bar instead.",
            variant: "destructive",
          },
    )
  }

  const handleCopyPortalLink = async () => {
    const ok = await copyText(portalUrlFor(invoiceId))
    toast(
      ok
        ? {
            title: "Portal link copied",
            description:
              "Share it with the customer — they'll sign in with a one-time email link.",
          }
        : {
            title: "Could not copy",
            description: "Copy the URL from the address bar instead.",
            variant: "destructive",
          },
    )
  }

  return (
    <DropdownMenuContent align="end">
      <DropdownMenuItem asChild>
        <Link href={`/invoices/${invoiceId}`}>View</Link>
      </DropdownMenuItem>
      {actions.has("resend") && allowMutating && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            handleResend()
          }}
          disabled={resendBusy}
        >
          {/* A never-delivered (EMAIL_FAILED) row was never emailed, so
              it offers "Send email" (a first send), not "Resend email". */}
          {isFirstSend ? "Send email" : "Resend email"}
        </DropdownMenuItem>
      )}
      {actions.has("sendEmail") && allowMutating && (
        // A `created` invoice was issued without emailing the customer;
        // this delivers the email and moves it to `sent` (routes to /send/,
        // like the EMAIL_FAILED first-send path).
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            handleSendEmail()
          }}
          disabled={sendMutation.isPending}
        >
          Send email
        </DropdownMenuItem>
      )}
      {showEdit && (
        <DropdownMenuItem asChild>
          <Link href={`/invoices/${invoiceId}/amend`}>Edit</Link>
        </DropdownMenuItem>
      )}
      {actions.has("reissue") && allowMutating && (
        <DropdownMenuItem asChild>
          <Link href={`/invoices/new?reissue_from=${invoiceId}`}>
            Reissue
          </Link>
        </DropdownMenuItem>
      )}
      {actions.has("copyLink") && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            void handleCopyLink()
          }}
        >
          Copy payment link
        </DropdownMenuItem>
      )}
      {actions.has("copyPortalLink") && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            void handleCopyPortalLink()
          }}
        >
          Copy portal link
        </DropdownMenuItem>
      )}
      {showReceipt && (
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault()
            void copyReceiptLink(receiptPathFor(invoiceId))
          }}
        >
          Copy receipt link
        </DropdownMenuItem>
      )}
      {actions.has("downloadPdf") && (
        <DropdownMenuItem asChild>
          <a href={pdfUrlFor(invoiceId)} download>
            Download PDF
          </a>
        </DropdownMenuItem>
      )}
      {showVoid && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-destructive">
            <Link href={`/invoices/${invoiceId}/void`}>Void</Link>
          </DropdownMenuItem>
        </>
      )}
      {actions.has("refund") && allowMutating && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-destructive">
            <Link href={`/invoices/${invoiceId}/refund`}>Refund</Link>
          </DropdownMenuItem>
        </>
      )}
      {actions.has("delete") && allowMutating && (
        // Draft-only. ``allowMutating`` is ``canVoid`` == ``canDelete``
        // (not-Finance; SR must own; Manager any), so it doubles as the
        // delete ownership gate.
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild className="text-destructive">
            <Link href={`/invoices/${invoiceId}/delete`}>Delete draft</Link>
          </DropdownMenuItem>
        </>
      )}
      {hubspotDealUrl && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <a
              href={hubspotDealUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in HubSpot
              <ExternalLink className="h-3 w-3 ml-2" />
            </a>
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  )
}
